<template>
  <div
    class="bg-white flex flex-col"
    :class="isColivingHubCompactLayout ? 'h-dvh overflow-hidden' : 'min-h-screen'"
  >
    <!-- Navigation wrapper - Fixed position, full width -->
    <header v-if="!isWidgetMode" :class="['w-full fixed top-0 left-0 z-[110] transition-all duration-300', $route.path === '/' ? 'bg-gradient-to-b from-black/60 via-black/30 to-transparent sm:backdrop-blur-[2px]' : 'bg-white border-b border-gray-100']">
      <!-- Centered container for navigation content -->
      <div class="mx-auto px-4">
        <!-- Skeleton Loader (inside container) -->
        <div v-if="!isInitialAuthCheckComplete" class="h-16 flex items-center justify-between">
          <div class="flex-shrink-0 flex items-center">
            <div class="text-xl font-bold text-indigo-600">{{ agencyName }}</div>
          </div>
          <div class="animate-pulse h-8 w-24 bg-gray-200 rounded"></div>
        </div>
        
        <!-- Real Navigation (inside container) -->
        <nav v-else class="h-16">
          <div class="flex justify-between items-center h-full">
            <!-- Izquierda: Logo + Contratos + Buscador -->
            <div class="flex items-center space-x-3 md:space-x-4 lg:space-x-6">
              <!-- Flecha de volver (solo visible en páginas de coliving) -->
              <router-link 
                v-if="$route.path.startsWith('/colivings/')"
                :to="currentUserType === 'agent' ? '/agent-dashboard' : '/contracts'"
                class="flex-shrink-0 text-gray-600 hover:text-gray-900 transition-colors"
                title="Volver"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </router-link>
              
              <!-- Logo -->
              <div class="flex-shrink-0 flex items-center">
                <router-link :to="!isAuthenticated ? '/' : (currentUserType === 'agent' ? '/agent-dashboard' : (currentUserType === 'managedOwner' ? '/owner-dashboard' : '/contracts'))" class="flex flex-col items-start">
                  <!-- ACTUALIZADO: Logo con carga de información del agente -->
                  <div v-if="!isAgentInfoReady && currentUserType === 'agent'" class="animate-pulse">
                    <div class="h-6 w-20 bg-gray-200 rounded"></div>
                  </div>
                  <div v-else-if="shouldShowAgencyLogo" class="flex flex-col">
                    <span class="text-lg md:text-xl font-bold text-indigo-600">{{ agencyName }}</span>
                    <span class="text-xs text-gray-500 -mt-1">powered by Coolliving</span>
                  </div>
                  <div v-else :class="['text-lg md:text-xl font-bold transition-colors duration-300', $route.path === '/' ? 'text-white' : 'text-indigo-600']">Coolliving</div>
                </router-link>
              </div>
              <!-- Enlaces Contratos y Buscador (Desktop) -->
              <div class="hidden sm:flex sm:space-x-2 md:space-x-3 lg:space-x-4">
                <router-link v-if="isInitialAuthCheckComplete && isAuthenticated && currentUserType === 'agent'" to="/agent-dashboard" :class="[
                  $route.path.startsWith('/agent-dashboard')
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  'inline-flex items-center px-1 pt-1 border-b-2 text-xs md:text-sm font-medium whitespace-nowrap'
                ]">
                  {{ $t('navigation.agentDashboard') }}
                </router-link>
                <router-link v-else-if="isAuthenticated && currentUserType === 'managedOwner'" to="/owner-dashboard" :class="[
                  $route.path.startsWith('/owner-dashboard')
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  'inline-flex items-center px-1 pt-1 border-b-2 text-xs md:text-sm font-medium whitespace-nowrap'
                ]">
                  Panel de Propietario
                </router-link>
                <router-link v-else-if="isAuthenticated" to="/contracts" :class="[
                  $route.path.startsWith('/contracts')
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  'inline-flex items-center px-1 pt-1 border-b-2 text-xs md:text-sm font-medium whitespace-nowrap'
                ]">
                  {{ $t('navigation.contracts') }}
                </router-link>

                <router-link v-if="isInitialAuthCheckComplete && isAuthenticated && currentUserType === 'applicant'" to="/user-dashboard" :class="[
                  $route.path.startsWith('/user-dashboard')
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  'inline-flex items-center px-1 pt-1 border-b-2 text-xs md:text-sm font-medium whitespace-nowrap'
                ]">
                  {{ $t('navigation.userPanel') }}
                </router-link>
                
               
                <!-- <div v-if="currentUserType !== 'agent'" class="relative">
                <button @click="toggleExtensionBanner" :class="[
                    'inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium border-transparent',
                  showExtensionBanner
                      ? 'text-gray-900'
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                ]">
                  Extensión
                </button>
                  <span v-show="showExtensionBanner" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-500"></span>
                </div>  -->
              </div>
            </div>
            
            <!-- Barra de navegación central (Resto de enlaces) - Oculto en md, visible en lg+ -->
            <div class="hidden lg:flex lg:items-center lg:justify-center flex-1 px-2 md:px-4">
              <div class="flex items-center space-x-1">
                <!-- Quitar Contratos y Buscador de aquí -->

                <router-link v-if="!isAuthenticated"
                   to="/"
                   @click="handleNavClick('navbar_center', 'home', '/')"
                   :class="[
                     'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200',
                     $route.path === '/'
                       ? 'text-white bg-white/15'
                       : $route.path === '/' ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                   ]">
                  {{ $t('navigation.contracts') }}
                </router-link>
                
                <router-link v-if="!isAuthenticated" 
                   to="/#profiles-section" 
                   @click="handleNavClick('navbar_center', 'profiles', '/#profiles-section')"
                   :class="[
                     'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200',
                     $route.path === '/' && $route.hash === '#profiles-section'
                       ? 'text-white bg-white/15'
                       : $route.path === '/' ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                   ]">
                  {{ $t('navigation.profiles') }}
                </router-link>

                <button
                  v-if="!isAuthenticated"
                  type="button"
                  @click="handlePartnerServicesClick('navbar')"
                  :class="[
                    'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200',
                    $route.path === '/' ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  ]"
                >
                  {{ $t('navigation.partnerServices') }}
                </button>

                <router-link v-if="!isAuthenticated" 
                   to="/blog" 
                   @click="handleNavClick('navbar_center', 'blog', '/blog')"
                   :class="[
                     'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200',
                     $route.path.startsWith('/blog')
                       ? 'text-gray-900 bg-gray-100'
                       : $route.path === '/' ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                   ]">
                  {{ $t('navigation.blog') }}
                </router-link>

                <router-link v-if="!isAuthenticated"
                   to="/sobre-nosotros"
                   @click="handleNavClick('navbar_center', 'about', '/sobre-nosotros')"
                   :class="[
                     'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200',
                     $route.path === '/sobre-nosotros'
                       ? 'text-gray-900 bg-gray-100'
                       : $route.path === '/' ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                   ]">
                  {{ $t('navigation.aboutUs') }}
                </router-link>

                <router-link v-if="!isAuthenticated"
                   to="/contacto"
                   @click="handleNavClick('navbar_center', 'contact', '/contacto')"
                   :class="[
                     'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200',
                     $route.path === '/contacto'
                       ? 'text-gray-900 bg-gray-100'
                       : $route.path === '/' ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                   ]">
                  {{ $t('navigation.footer.contact') }}
                </router-link>
              </div>
            </div>
            
            <!-- Área de usuario a la derecha (desktop) -->
            <div class="hidden sm:flex sm:items-center sm:space-x-1 md:space-x-2 lg:space-x-3">
              <!-- Botón "Encuentra tu coliving" (visible para NO autenticados Y para applicants) -->
              <router-link 
                v-if="!isAuthenticated || currentUserType === 'applicant'"
                to="/encuentratucoliving"
                @click="handleNavClick('navbar_right', 'find_coliving', '/encuentratucoliving')"
                :class="[
                  'px-2 md:px-3 py-1.5 text-xs md:text-sm font-medium transition-all duration-200 text-center inline-flex items-center whitespace-nowrap',
                  $route.path === '/' ? 'text-white/90 hover:text-white' : 'text-indigo-600'
                ]">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-1.5" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {{ $t('navigation.findColiving') }}
              </router-link>

              <!-- Botón de chat en navbar (solo para agentes y applicants autenticados) -->
              <ChatNavbarButton 
                v-if="isAuthenticated && (currentUserType === 'agent' || currentUserType === 'applicant')"
              />
              
              <!-- Componente de notificaciones (solo para usuarios autenticados) -->
              <NotificationsMenu
                v-if="isAuthenticated"
                :notifications="notifications"
                @mark-read="handleMarkAsRead"
                @mark-all-read="handleMarkAllAsRead"
                @delete="handleDeleteNotification"
                @view="handleViewNotification"
              />
              
              <!-- Dynamic login/logout button -->
              <div v-if="!isAuthenticated" class="flex space-x-1 md:space-x-2 lg:space-x-3 items-center">
                
                <!-- Dropdown: Gestiona tu coliving - Oculto en md, visible en lg+ -->
                <div class="relative dropdown-manage-container hidden lg:block">
                  <button
                    @click="toggleManageDropdown"
                    :class="[
                      'px-2 md:px-3 py-1.5 text-xs md:text-sm font-medium transition-all duration-200 text-center inline-flex items-center whitespace-nowrap',
                      $route.path === '/' ? 'text-white/90 hover:text-white' : 'text-indigo-600 hover:text-indigo-800'
                    ]">
                    {{ $t('navigation.manageColiving') }}
                    <svg class="ml-1 h-3 w-3 md:h-4 md:w-4 transition-transform duration-200" :class="{ 'rotate-180': showManageDropdown }" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </button>
                  
                  <!-- Dropdown Menu -->
                  <div v-show="showManageDropdown" 
                    class="absolute right-0 mt-2 w-64 rounded-xl shadow-lg bg-white border border-gray-200 z-50">
                    <div class="py-1">
                      <router-link 
                        to="/signup?userType=agent"
                        @click="showManageDropdown = false"
                        class="group flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 transition-colors">
                        <svg class="mr-3 h-5 w-5 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <div class="text-left">
                          <p class="font-medium text-gray-900">{{ $t('navigation.manageColiving') }}</p>
                          <p class="text-xs text-gray-500">{{ $t('navigation.createAgentAccount') }}</p>
                        </div>
                      </router-link>
                      
                      <router-link 
                        to="/encuentra-tu-gestor"
                        @click="showManageDropdown = false"
                        class="group flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 transition-colors">
                        <svg class="mr-3 h-5 w-5 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        <div class="text-left">
                          <p class="font-medium text-gray-900">{{ $t('navigation.haveProperty') }}</p>
                          <p class="text-xs text-gray-500">Encuentra un gestor para tu propiedad</p>
                        </div>
                      </router-link>
                      
                      <button 
                        @click="openAddColivingModal"
                        class="group flex items-center text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 transition-colors">
                        <svg class="mr-3 h-5 w-5 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <div class="text-left">
                          <p class="font-medium text-gray-900">{{ $t('navigation.addYourColiving') }}</p>
                          <p class="text-xs text-gray-500">{{ $t('navigation.listYourProperty') }}</p>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
                
                <!-- DESACTIVADO: Redundante con "Gestiona tu coliving" -->
                <!-- <router-link 
                  :to="$route.path.startsWith('/encuentratucoliving') || $route.path.startsWith('/coliving/') ? '/signup?userType=applicant' : '/signup'"
                  class="text-indigo-600 px-2 md:px-3 py-1.5 text-xs md:text-sm font-medium transition-all duration-200 text-center whitespace-nowrap">
                  {{ $t('navigation.startNow') }}
                </router-link> -->
                <router-link to="/login"
                  :class="[
                    'px-4 py-1.5 text-sm font-medium transition-all duration-200 text-center rounded-full whitespace-nowrap',
                    $route.path === '/' 
                      ? 'text-white bg-white/10 border border-white/20 hover:bg-white/20 backdrop-blur-sm' 
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  ]">
                {{ $t('navigation.login') }}
                </router-link>
              </div>
              <div v-else class="relative dropdown-container">
                <button @click="toggleUserMenu"
                  class="flex items-center space-x-1 md:space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none whitespace-nowrap">
                  <span class="text-xs md:text-sm font-medium">{{ user?.displayName || user?.email }}</span>
                  <svg class="h-4 w-4 md:h-5 md:w-5" :class="{ 'transform rotate-180': isUserMenuOpen }"
                    xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd" />
                  </svg>
                </button>
  
                <!-- Dropdown menu -->
                <div v-if="isUserMenuOpen"
                  class="absolute right-0 mt-2 w-56 rounded-xl shadow-lg bg-white border border-gray-200 divide-y divide-gray-100">
                  <div class="py-1">
                   
                    <!-- OCULTO PARA AGENTES Y APPLICANTS: Los agentes usan /agent-dashboard/profile y los applicants usan /user-dashboard -->
                    <router-link v-if="currentUserType !== 'agent' && currentUserType !== 'applicant'" to="/profile" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      @click="isUserMenuOpen = false">
                      {{ $t('navigation.viewProfile') }}
                    </router-link>
                    
                    <router-link v-if="currentUserType === 'admin'" to="/admin-panel" 
                      class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      @click="isUserMenuOpen = false">
                      {{ $t('navigation.adminPanel') }}
                    </router-link>
                  </div>
                  <div class="py-1">
                    <button @click="handleLogout"
                      class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100">
                      {{ $t('navigation.logout') }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Selector de región - Oculto en md, visible en lg+ -->
              <div class="hidden lg:block">
                <RegionSelector />
              </div>

              <!-- Botón hamburguesa para MD (tablet) - visible solo entre sm y lg, al final -->
              <button 
                v-if="!isAuthenticated"
                @click="mdMenuOpen = !mdMenuOpen"
                :class="[
                  'sm:flex lg:hidden p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ml-2 transition-colors duration-300',
                  $route.path === '/' ? 'text-white/80 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                ]">
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path v-if="!mdMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                  <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <!-- Menú móvil toggle (AHORA DENTRO DEL CONTENEDOR FLEX PRINCIPAL) -->
            <div class="-mr-2 flex items-center sm:hidden">
              <!-- Chat móvil (solo para agentes y applicants autenticados) -->
              <ChatNavbarButton 
                v-if="isAuthenticated && (currentUserType === 'agent' || currentUserType === 'applicant')"
                class="mr-2" 
              />
              
              <!-- Notificaciones móviles -->
              <NotificationsMenu
                v-if="isAuthenticated"
                :notifications="notifications"
                @mark-read="handleMarkAsRead"
                @mark-all-read="handleMarkAllAsRead"
                @delete="handleDeleteNotification"
                @view="handleViewNotification"
                class="mr-2"
              />
              
              <!-- Selector de región compacto (banderas) - Solo para no autenticados -->
              <div v-if="!isAuthenticated" class="relative mr-1" ref="regionPickerMobileRef">
                <button
                  @click="showRegionPickerMobile = !showRegionPickerMobile"
                  :class="[
                    'flex items-center gap-1 px-2 py-1.5 rounded-full transition-all duration-200',
                    $route.path === '/' ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                  ]"
                  aria-label="Seleccionar región"
                >
                  <span class="text-xl leading-none">{{ regionFlag }}</span>
                  <svg
                    class="w-3 h-3 transition-transform duration-200"
                    :class="[showRegionPickerMobile ? 'rotate-180' : '', $route.path === '/' ? 'text-white/70' : 'text-gray-400']"
                    fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>

                <!-- Mini dropdown de regiones -->
                <transition
                  enter-active-class="transition ease-out duration-150"
                  enter-from-class="opacity-0 scale-95 -translate-y-1"
                  enter-to-class="opacity-100 scale-100 translate-y-0"
                  leave-active-class="transition ease-in duration-100"
                  leave-from-class="opacity-100 scale-100 translate-y-0"
                  leave-to-class="opacity-0 scale-95 -translate-y-1"
                >
                  <div v-if="showRegionPickerMobile"
                    class="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl ring-1 ring-black/5 z-[200] overflow-hidden"
                    style="min-width: 180px">
                    <button @click="changeRegionMobile('es')"
                      class="flex items-center gap-3 w-full px-4 py-2.5 text-sm transition-colors"
                      :class="regionId === 'es' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50'">
                      <span class="text-lg">🇪🇸</span>
                      <div class="text-left flex-1">
                        <div class="font-medium leading-tight">España</div>
                        <div class="text-xs text-gray-400 leading-tight">Madrid, Barcelona...</div>
                      </div>
                      <svg v-if="regionId === 'es'" class="w-4 h-4 text-indigo-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </button>
                    <button @click="changeRegionMobile('us')"
                      class="flex items-center gap-3 w-full px-4 py-2.5 text-sm transition-colors border-t border-gray-50"
                      :class="regionId === 'us' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50'">
                      <span class="text-lg">🇺🇸</span>
                      <div class="text-left flex-1">
                        <div class="font-medium leading-tight">United States</div>
                        <div class="text-xs text-gray-400 leading-tight">New York</div>
                      </div>
                      <svg v-if="regionId === 'us'" class="w-4 h-4 text-indigo-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </button>
                    <button @click="changeRegionMobile('uk')"
                      class="flex items-center gap-3 w-full px-4 py-2.5 text-sm transition-colors border-t border-gray-50"
                      :class="regionId === 'uk' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50'">
                      <span class="text-lg">🇬🇧</span>
                      <div class="text-left flex-1">
                        <div class="font-medium leading-tight">United Kingdom</div>
                        <div class="text-xs text-gray-400 leading-tight">London</div>
                      </div>
                      <svg v-if="regionId === 'uk'" class="w-4 h-4 text-indigo-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </transition>
              </div>

              <!-- Mobile menu button/user info -->
              <div v-if="!isAuthenticated">
                <button @click="toggleMobileMenu" type="button"
                  :class="[
                    'inline-flex items-center justify-center p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500',
                    $route.path === '/' ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-500 hover:bg-gray-100'
                  ]"
                  aria-controls="mobile-menu" aria-expanded="false">
                  <span class="sr-only">{{ $t('navigation.openMainMenu') }}</span>
                  <!-- Icon when menu is closed -->
                  <svg :class="{ 'hidden': mobileMenuOpen, 'block': !mobileMenuOpen }" class="h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  <!-- Icon when menu is open -->
                  <svg :class="{ 'block': mobileMenuOpen, 'hidden': !mobileMenuOpen }" class="h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div v-else>
                <button @click="toggleMobileMenu" 
                  class="flex items-center space-x-1 text-gray-700 hover:text-gray-900 focus:outline-none p-1 rounded-md hover:bg-gray-100">
                  <span class="text-sm font-medium truncate max-w-[120px]">{{ user?.displayName || user?.email }}</span>
                  <svg :class="{ 'transform rotate-180': mobileMenuOpen, 'transform rotate-0': !mobileMenuOpen }" 
                    class="h-5 w-5 transition-transform duration-200"
                    xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </nav>

        <!-- MD/Tablet menu (visible solo entre sm y lg) -->
        <div v-show="mdMenuOpen" class="hidden sm:block lg:hidden fixed inset-0 z-[120] bg-white" style="top: 64px;">
          <div class="h-full overflow-y-auto">
            <div class="pt-4 pb-3 space-y-1 px-5">
              <!-- Enlaces de navegación landing (solo para no autenticados) -->
              <router-link v-if="!isAuthenticated" to="/"
                 class="block px-4 py-2.5 rounded-lg text-base font-medium transition-colors"
                 :class="$route.path === '/' ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'"
                 @click="mdMenuOpen = false">
                {{ $t('navigation.contracts') }}
              </router-link>

              <router-link v-if="!isAuthenticated" 
                 to="/#profiles-section" 
                 class="block px-4 py-2.5 rounded-lg text-base font-medium transition-colors"
                 :class="$route.path === '/' && $route.hash === '#profiles-section' ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'"
                 @click="mdMenuOpen = false">
                {{ $t('navigation.profiles') }}
              </router-link>

               <button
                 v-if="!isAuthenticated"
                 type="button"
                 class="block w-full text-left px-4 py-2.5 rounded-lg text-base font-medium transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                 @click="mdMenuOpen = false; handlePartnerServicesClick('navbar')"
               >
                {{ $t('navigation.partnerServices') }}
              </button>

              <router-link v-if="!isAuthenticated" 
                 to="/blog" 
                 class="block px-4 py-2.5 rounded-lg text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                 @click="mdMenuOpen = false">
                {{ $t('navigation.blog') }}
              </router-link>

              <router-link v-if="!isAuthenticated" to="/sobre-nosotros"
                 class="block px-4 py-2.5 rounded-lg text-base font-medium transition-colors"
                 :class="$route.path === '/sobre-nosotros' ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'"
                 @click="mdMenuOpen = false">
                {{ $t('navigation.aboutUs') }}
              </router-link>

              <router-link v-if="!isAuthenticated" to="/contacto"
                 class="block px-4 py-2.5 rounded-lg text-base font-medium transition-colors"
                 :class="$route.path === '/contacto' ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'"
                 @click="mdMenuOpen = false">
                {{ $t('navigation.footer.contact') }}
              </router-link>

              <!-- Divider -->
              <div v-if="!isAuthenticated" class="h-px bg-gray-100 my-3"></div>

              <!-- Enlaces de acción (Encuentra/Gestiona/Login) -->
              <router-link v-if="!isAuthenticated || currentUserType === 'applicant'" 
                 to="/encuentratucoliving"
                 class="block px-4 py-2.5 rounded-lg text-base font-medium text-gray-900 hover:bg-gray-50 transition-colors"
                 @click="mdMenuOpen = false">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {{ $t('navigation.findColiving') }}
                </div>
              </router-link>

              <button v-if="!isAuthenticated"
                 @click="showManageDropdownInMd"
                 class="w-full text-left px-4 py-2.5 rounded-lg text-base font-medium text-gray-900 hover:bg-gray-50 transition-colors">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {{ $t('navigation.manageColiving') }}
                </div>
              </button>

              <!-- Divider + CTAs -->
              <div v-if="!isAuthenticated" class="space-y-2 pt-4 mt-2 border-t border-gray-100">
                <router-link v-if="!isAuthenticated" 
                   to="/login"
                   class="block w-full text-center px-4 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-full hover:bg-gray-800 transition-colors"
                   @click="mdMenuOpen = false">
                  {{ $t('navigation.login') }}
                </router-link>
              </div>

              <!-- Selector de región -->
              <div class="pt-3 mt-2 border-t border-gray-100">
                <RegionSelectorMobile @region-changed="mdMenuOpen = false" />
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile menu (AHORA DENTRO DEL CONTENEDOR CENTRADO, DESPUÉS DEL NAV) -->
        <div v-show="mobileMenuOpen" class="sm:hidden fixed inset-0 z-[150] bg-white flex flex-col" id="mobile-menu" style="top: 64px;">
          <!-- Contenido principal con scroll -->
          <div class="flex-1 overflow-y-auto">
            <div class="pt-4 pb-3 px-4 space-y-1">
            <!-- 1️⃣ Dashboard links (para usuarios autenticados) -->
            <router-link v-if="isInitialAuthCheckComplete && isAuthenticated && currentUserType === 'agent'" to="/agent-dashboard" :class="[
              $route.path.startsWith('/agent-dashboard')
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              'block px-4 py-2.5 rounded-lg text-base font-medium transition-colors'
            ]" @click="mobileMenuOpen = false">
              {{ $t('navigation.agentDashboard') }}
            </router-link>
            <router-link v-else-if="isAuthenticated && currentUserType === 'managedOwner'" to="/owner-dashboard" :class="[
              $route.path.startsWith('/owner-dashboard')
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              'block px-4 py-2.5 rounded-lg text-base font-medium transition-colors'
            ]" @click="mobileMenuOpen = false">
              Panel de Propietario
            </router-link>
            <router-link v-else-if="isAuthenticated" to="/contracts" :class="[
              $route.path.startsWith('/contracts')
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              'block px-4 py-2.5 rounded-lg text-base font-medium transition-colors'
            ]" @click="mobileMenuOpen = false">
              {{ $t('navigation.contracts') }}
            </router-link>

            <router-link v-if="isInitialAuthCheckComplete && isAuthenticated && currentUserType === 'applicant'" to="/user-dashboard" :class="[
              $route.path.startsWith('/user-dashboard')
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              'block px-4 py-2.5 rounded-lg text-base font-medium transition-colors'
            ]" @click="mobileMenuOpen = false">
              {{ $t('navigation.userPanel') }}
            </router-link>

            <!-- 2️⃣ Enlaces de landing (solo para no autenticados) -->
            <router-link v-if="!isAuthenticated" to="/" :class="[
              $route.path === '/'
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              'block px-4 py-2.5 rounded-lg text-base font-medium transition-colors'
            ]" @click="mobileMenuOpen = false">
              {{ $t('navigation.contracts') }}
            </router-link>

            <router-link v-if="!isAuthenticated"
               to="/#profiles-section"
               class="block px-4 py-2.5 rounded-lg text-base font-medium transition-colors cursor-pointer"
               :class="$route.path === '/' && $route.hash === '#profiles-section' ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'"
               @click="mobileMenuOpen = false">
              {{ $t('navigation.profiles') }}
            </router-link>

            <button
              v-if="!isAuthenticated"
              type="button"
              class="block w-full text-left px-4 py-2.5 rounded-lg text-base font-medium transition-colors text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              @click="mobileMenuOpen = false; handlePartnerServicesClick('navbar')"
            >
              {{ $t('navigation.partnerServices') }}
            </button>

            <router-link v-if="!isAuthenticated" to="/blog" :class="[
              $route.path.startsWith('/blog')
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              'block px-4 py-2.5 rounded-lg text-base font-medium transition-colors'
            ]" @click="mobileMenuOpen = false">
              {{ $t('navigation.blog') }}
            </router-link>

            <router-link v-if="!isAuthenticated" to="/sobre-nosotros" :class="[
              $route.path === '/sobre-nosotros'
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              'block px-4 py-2.5 rounded-lg text-base font-medium transition-colors'
            ]" @click="mobileMenuOpen = false">
              {{ $t('navigation.aboutUs') }}
            </router-link>

            <router-link v-if="!isAuthenticated" to="/contacto" :class="[
              $route.path === '/contacto'
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              'block px-4 py-2.5 rounded-lg text-base font-medium transition-colors'
            ]" @click="mobileMenuOpen = false">
              {{ $t('navigation.footer.contact') }}
            </router-link>
            
            <!-- Divider -->
            <div class="h-px bg-gray-100 my-2"></div>

            <!-- 3️⃣ "Encuentra tu coliving" (para NO autenticados Y applicants) -->
            <router-link
              v-if="!isAuthenticated || currentUserType === 'applicant'"
              to="/encuentratucoliving"
              @click="mobileMenuOpen = false"
              class="block px-4 py-2.5 rounded-lg text-base font-medium text-gray-900 hover:bg-gray-50 transition-colors">
              <span class="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2.5 text-gray-400" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {{ $t('navigation.findColiving') }}
              </span>
            </router-link>
            
            <!-- 4️⃣ "Añade tu coliving" (para NO autenticados Y applicants) -->
            <button 
              v-if="!isAuthenticated || currentUserType === 'applicant'"
              @click="isAddColivingModalOpen = true; mobileMenuOpen = false"
              class="w-full text-left block px-4 py-2.5 rounded-lg text-base font-medium text-gray-900 hover:bg-gray-50 transition-colors">
              <span class="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-5 w-5 mr-2.5 text-gray-400">
                  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
                </svg>
                {{ $t('navigation.addColiving') }}
              </span>
            </button>
            
            <!-- 5️⃣ Botones para NO autenticados (Gestiona tu coliving, Login) -->
            <div v-if="!isAuthenticated" class="space-y-2 pt-4 mt-2 border-t border-gray-100">
              <router-link
                to="/signup?userType=agent"
                @click="mobileMenuOpen = false"
                class="block w-full text-center px-4 py-2.5 text-sm font-medium text-gray-900 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors">
                {{ $t('navigation.manageColiving') }}
              </router-link>
              
              <router-link
                to="/login"
                @click="mobileMenuOpen = false"
                class="block w-full text-center px-4 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-full hover:bg-gray-800 transition-colors">
                {{ $t('navigation.login') }}
              </router-link>
            </div>
            
            <!-- 6️⃣ Profile/Admin panel (para autenticados, EXCEPTO applicants que usan UserPanel) -->
            <router-link v-if="isAuthenticated && currentUserType !== 'agent' && currentUserType !== 'applicant'" to="/profile" :class="[
              $route.path === '/profile'
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              'block px-4 py-2.5 rounded-lg text-base font-medium mt-2 border-t border-gray-100 pt-4 transition-colors'
            ]" @click="mobileMenuOpen = false">
              {{ $t('navigation.profile') }}
            </router-link>
            <router-link v-if="isAuthenticated && currentUserType === 'admin'" to="/admin-panel" :class="[
              $route.path === '/admin-panel'
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              'block px-4 py-2.5 rounded-lg text-base font-medium transition-colors'
            ]" @click="mobileMenuOpen = false">
              {{ $t('navigation.adminPanel') }}
            </router-link>
            
            <!-- 7️⃣ "Cerrar Sesión" (AL FINAL para autenticados) -->
            <div v-if="isAuthenticated" class="pt-4 mt-2 border-t border-gray-100">
              <button @click="logoutAndCloseMenu" 
                class="block w-full text-left text-sm font-medium text-red-500 hover:bg-red-50 px-4 py-2.5 rounded-lg transition-colors">
                {{ $t('navigation.logout') }}
              </button>
            </div>
            
            <!-- 8️⃣ Selector de región horizontal - Encima del footer -->
            <div class="border-t border-gray-100 px-4 py-3 mt-2">
              <RegionSelectorMobile @regionChanged="mobileMenuOpen = false" />
            </div>
          </div>
          </div>
          
          <!-- 9️⃣ Footer fijo con redes sociales -->
          <div class="border-t border-gray-100 bg-white">
            <div class="px-4 py-4">
              <div class="flex items-center justify-center space-x-8">
                <a 
                  href="https://www.instagram.com/coolliving.io/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  @click="mobileMenuOpen = false"
                  class="text-gray-400 hover:text-indigo-600 transition-colors duration-200"
                  aria-label="Instagram">
                  <svg class="w-7 h-7" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill-rule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clip-rule="evenodd" />
                  </svg>
                </a>
                <a 
                  href="https://www.linkedin.com/company/coolliving-io" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  @click="mobileMenuOpen = false"
                  class="text-gray-400 hover:text-indigo-600 transition-colors duration-200"
                  aria-label="LinkedIn">
                  <svg class="w-7 h-7" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
              
              <!-- Enlaces legales -->
              <div class="mt-3 flex flex-wrap justify-center gap-x-3 gap-y-1 text-center">
                <router-link
                  to="/privacy-policy"
                  @click="mobileMenuOpen = false"
                  class="text-xs text-gray-400 hover:text-indigo-600 transition-colors duration-200">
                  {{ $t('navigation.footer.privacyPolicy') }}
                </router-link>
                <router-link
                  to="/terms"
                  @click="mobileMenuOpen = false"
                  class="text-xs text-gray-400 hover:text-indigo-600 transition-colors duration-200">
                  {{ $t('navigation.footer.terms') }}
                </router-link>
                <router-link
                  to="/cookies"
                  @click="mobileMenuOpen = false"
                  class="text-xs text-gray-400 hover:text-indigo-600 transition-colors duration-200">
                  {{ $t('navigation.footer.cookies') }}
                </router-link>
                <router-link
                  to="/aviso-legal"
                  @click="mobileMenuOpen = false"
                  class="text-xs text-gray-400 hover:text-indigo-600 transition-colors duration-200">
                  {{ $t('navigation.legalNotice') }}
                </router-link>
                <router-link
                  to="/leads-marketplace"
                  @click="mobileMenuOpen = false"
                  class="text-xs text-gray-400 hover:text-indigo-600 transition-colors duration-200">
                  {{ $t('navigation.footer.forOperators') }}
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Chrome Extension Banner (Solo Desktop) - Justo debajo de la navbar -->
    <!-- DESACTIVADO: Banner de extensión de Chrome -->
    <!--
    <transition 
      name="banner-slide" 
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 transform -translate-y-full"
      enter-to-class="opacity-100 transform translate-y-0"
      leave-active-class="transition-all duration-300 ease-in"
      leave-from-class="opacity-100 transform translate-y-0"
      leave-to-class="opacity-0 transform -translate-y-full"
    >
      <div v-if="showExtensionBanner && extensionBannerDelay && !isWidgetMode" class="hidden md:block fixed top-16 left-0 right-0 z-[99] w-full">
      <ChromeExtensionBanner 
        :is-for-owner="route.path.includes('/vende-o-alquila') || route.path === '/'" 
        :initial-show-info="false"
          @close="dismissExtensionBanner"
      />
    </div>
    </transition>
    -->

    <!-- Lead Magnet Banner - Solo en búsqueda de colivings y usuarios NO autenticados -->
    <transition 
      name="banner-slide" 
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 transform -translate-y-full"
      enter-to-class="opacity-100 transform translate-y-0"
      leave-active-class="transition-all duration-300 ease-in"
      leave-from-class="opacity-100 transform translate-y-0"
      leave-to-class="opacity-0 transform -translate-y-full"
    >
      <div v-if="showLeadMagnetBanner && !isAuthenticated && !isWidgetMode" 
        class="fixed top-16 left-0 right-0 z-[99] w-full">
        <LeadMagnetBanner 
          :city-name="currentCityFromRoute"
          @open-lead-magnet="handleOpenLeadMagnetFromBanner"
          @close="dismissLeadMagnetBanner"
        />
      </div>
    </transition>

    <!-- Main content - Con ajuste de padding para el banner de lead magnet -->
    <div
      class="flex-grow"
      :class="{
        'flex flex-col min-h-0 overflow-hidden pt-16 pb-0': isColivingHubCompactLayout && !isWidgetMode,
        'pt-10': !isWidgetMode && !showLeadMagnetBanner && $route.path !== '/' && !isColivingHubCompactLayout,
        'pt-28': !isWidgetMode && showLeadMagnetBanner && $route.path !== '/' && !isColivingHubCompactLayout,
        'pt-0': $route.path === '/' && !isWidgetMode,
        'container mx-auto px-0 py-8': $route.path !== '/' && (!isMobile || $route.path !== '/') && !isWidgetMode && !isColivingHubCompactLayout,
        'w-full px-0': $route.path === '/' && !isWidgetMode,
        'landing-wrapper w-full px-0 mobile-content-wrapper': isMobile && $route.path === '/' && !isWidgetMode,
      }"
    >
      <router-view v-slot="{ Component, route }" :key="routerViewKey">
        <transition 
          :name="route.meta.transition || 'page'" 
          mode="out-in" 
          @before-leave="beforePageLeave" 
          @enter="onPageEnter"
        >
          <keep-alive :include="cachedRoutes">
            <component 
              :is="Component" 
              :key="componentKey"
              v-if="isInitialAuthCheckComplete || !route.meta.requiresAuth" 
            />
            <div v-else class="w-full flex justify-center items-center min-h-[300px]">
              <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          </keep-alive>
        </transition>
      </router-view>
    </div>

    <!-- Botón volver arriba — al lado del widget IA (bottom-3, ~192px desde la derecha) -->
    <button
      v-if="showScrollToTop"
      @click="scrollToTop"
      class="fixed bottom-3 z-50 w-12 h-12 bg-gray-900 hover:bg-gray-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
      style="right: 12.5rem"
      aria-label="Volver arriba"
    >
      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
      </svg>
    </button>

    <!-- Footer wrapper -->
    <div v-if="showFooter" class="bg-gray-800 w-full mt-auto" :class="{ 'hidden': isMobile && !mobileMenuOpen }">
      <footer class="max-w-5xl mx-auto px-4 sm:px-6 text-white py-6">
        <div class="flex flex-col items-center gap-3">
          <!-- Fila 1: enlaces principales -->
          <div class="flex justify-center items-center text-sm flex-wrap gap-x-4 gap-y-2">
            <router-link to="/" class="hover:text-gray-300 transition-colors">{{ $t('navigation.contracts') }}</router-link>
            <span class="text-gray-600">|</span>
            <router-link to="/sobre-nosotros" class="hover:text-gray-300 transition-colors">{{ $t('navigation.aboutUs') }}</router-link>
            <span class="text-gray-600">|</span>
            <router-link to="/contacto" class="hover:text-gray-300 transition-colors">{{ $t('navigation.footer.contact') }}</router-link>
            <span class="text-gray-600">|</span>
            <router-link to="/blog" class="hover:text-gray-300 transition-colors">{{ $t('navigation.blog') }}</router-link>
            <span class="text-gray-600">|</span>
            <router-link to="/leads-marketplace" class="hover:text-gray-300 transition-colors">
              {{ $t('navigation.footer.forOperators') }}
            </router-link>
            <span class="text-gray-600">|</span>
            <router-link to="/encuentra-tu-gestor" class="hover:text-gray-300 transition-colors">
              {{ $t('navigation.footer.findManager') }}
            </router-link>
            <span class="text-gray-600">|</span>
            <router-link to="/planes" class="hover:text-gray-300 transition-colors">
              {{ $t('navigation.footer.pricing') }}
            </router-link>
            <span class="text-gray-600">|</span>
            <router-link to="/calculadora-ahorro" class="hover:text-gray-300 transition-colors">
              {{ $t('navigation.footer.calculator') }}
            </router-link>
          </div>
          <!-- Fila 2: legales -->
          <div class="flex justify-center items-center text-xs flex-wrap gap-x-3 gap-y-1.5 text-gray-400">
            <p>{{ $t('navigation.footer.copyright') }}</p>
            <span class="text-gray-600">|</span>
            <router-link to="/privacy-policy" class="hover:text-gray-300 transition-colors">
              {{ $t('navigation.footer.privacyPolicy') }}
            </router-link>
            <span class="text-gray-600">|</span>
            <router-link to="/terms" class="hover:text-gray-300 transition-colors">
              {{ $t('navigation.footer.terms') }}
            </router-link>
            <span class="text-gray-600">|</span>
            <router-link to="/cookies" class="hover:text-gray-300 transition-colors">
              {{ $t('navigation.footer.cookies') }}
            </router-link>
            <span class="text-gray-600">|</span>
            <router-link to="/aviso-legal" class="hover:text-gray-300 transition-colors">
              {{ $t('navigation.legalNotice') }}
            </router-link>
          </div>
          <!-- Fila 3: redes sociales -->
          <div class="flex items-center gap-4 pt-1">
            <a 
              href="https://www.instagram.com/coolliving.io/" 
              target="_blank" 
              rel="noopener noreferrer"
              class="text-gray-400 hover:text-white transition-colors duration-200"
              aria-label="Instagram">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fill-rule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clip-rule="evenodd" />
              </svg>
            </a>
            <a 
              href="https://www.linkedin.com/company/coolliving-io" 
              target="_blank" 
              rel="noopener noreferrer"
              class="text-gray-400 hover:text-white transition-colors duration-200"
              aria-label="LinkedIn">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>
      </footer>
    </div>

    <!-- Footer legal mínimo en búsqueda (AdSense: enlaces visibles en todas las páginas) -->
    <LegalFooterStrip v-if="showLegalFooterStrip" />

    <!-- Modal de Onboarding desde Hero SimplifiedOwnerForm -->
    <OnboardingModal 
      :isOpen="isOnboardingModalOpen" 
      @close="handleOnboardingClose"
      @complete="handleOnboardingComplete"
    />

    <!-- Modal "Añade tu coliving" para usuarios no autenticados o applicants -->
    <UpgradeToProModal 
      :isVisible="isAddColivingModalOpen" 
      @close="isAddColivingModalOpen = false"
      @success="isAddColivingModalOpen = false"
    />

    <PartnerContactModal
      :isVisible="isPartnerServicesModalOpen"
      :initialService="partnerServicesInitialService"
      @close="isPartnerServicesModalOpen = false"
    />

    <AffiliatePartnerModal />

    <!-- Push opt-in (solo agent/applicant, solo si no ha concedido ni denegado) -->
    <PushOptIn v-if="!isWidgetMode" :user-type="currentUserType" />

    <!-- Otros componentes globales -->
    <CookiesBanner @accept="handleCookiesAccept" @reject="handleCookiesReject" />
    <!-- Notificación de actualización disponible (oculta temporalmente) -->
    <div v-if="false && updateAvailable" class="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white shadow-lg rounded-lg p-4 z-50 flex items-center justify-between">
      <div class="flex items-center">
        <svg class="w-6 h-6 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
        </svg>
        <div>
          <p class="text-gray-800 font-medium">{{ $t('navigation.update.available') }}</p>
          <p class="text-gray-600 text-sm">{{ $t('navigation.update.description') }}</p>
          <p v-if="swVersion" class="text-gray-500 text-xs mt-1">{{ $t('navigation.update.version') }} {{ swVersion }}</p>
        </div>
      </div>
      <button @click="refreshApp" class="ml-3 px-4 py-2 bg-white border border-indigo-200 text-indigo-600 text-sm font-medium rounded-full shadow-sm hover:bg-indigo-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
        {{ $t('navigation.update.button') }}
      </button>
    </div>
    <!-- Barra de estado de conexión - OCULTA -->
    <!-- <div v-if="!isOnline"
      class="fixed bottom-0 left-0 right-0 bg-red-600 text-white text-center py-2 text-sm font-medium z-50">
      {{ $t('navigation.offline.message') }}
    </div> -->
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
// ⚡ LAZY LOAD: No importar Firebase Auth inmediatamente (ahorra 2s en FCP)
// import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth'
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { db } from './firebase'
import CookiesBanner from './components/CookiesBanner.vue'
import OnboardingModal from './components/OnboardingModal.vue'
import RegionSelector from './components/RegionSelector.vue'
import RegionSelectorMobile from './components/RegionSelectorMobile.vue'
import NotificationsMenu from './components/NotificationsMenu.vue'
import ChromeExtensionBanner from './components/ChromeExtensionBanner.vue'
import ChatNavbarButton from './components/chat/ChatNavbarButton.vue'
import PushOptIn from './components/PushOptIn.vue'
import UpgradeToProModal from './components/agent/UpgradeToProModal.vue'
import AffiliatePartnerModal from './components/AffiliatePartnerModal.vue'
import LeadMagnetBanner from './components/LeadMagnetBanner.vue'
import LegalFooterStrip from './components/LegalFooterStrip.vue'
import { useI18n } from 'vue-i18n'
import Trans from './i18n/translation'
import {
  subscribeToNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification
} from './services/notificationService'
import { verifyAndFixUserType, getCurrentUserManagedOwner } from './services/managedOwnersService'
import { useAgentInfo, AGENT_ROLES } from './utils/agentRoleUtils'
import { openPartnerServicesModal, OPEN_PARTNER_SERVICES_EVENT } from './utils/partnerServicesModal'
import PartnerContactModal from './components/PartnerContactModal.vue'
import { removeFcmTokenFromBackend, setupForegroundMessages, isFcmEnabled, requestAndSavePushToken } from './utils/fcm'
import { useAnalytics } from '@/composables/useAnalytics'
import { useRegion } from '@/composables/useRegion'
import { SLUG_TO_NAME } from '../../shared/locationRegistry.cjs'

export default {
  name: 'App',
  components: {
    CookiesBanner,
    OnboardingModal,
    RegionSelector,
    RegionSelectorMobile,
    NotificationsMenu,
    ChromeExtensionBanner,
    ChatNavbarButton,
    UpgradeToProModal,
    PartnerContactModal,
    AffiliatePartnerModal,
    LeadMagnetBanner,
    LegalFooterStrip,
    PushOptIn,
  },
  setup() {
    const { t } = useI18n()
    const { trackNavClick } = useAnalytics()
    const { initializeRegion, regionId, regionFlag, setRegion } = useRegion()
    const route = useRoute()
    const router = useRouter()

    /** Mismas vistas hub que ColivingAdvancedSearch (encuentratucoliving, findyourcoliving, residencias). */
    const COLOIVING_HUB_PATH_PREFIXES = ['/encuentratucoliving', '/findyourcoliving', '/residencias']

    function stripRoutePath(full) {
      return (full || '').split('?')[0].split('#')[0]
    }

    function isColivingHubRoute(full) {
      const p = stripRoutePath(full)
      return COLOIVING_HUB_PATH_PREFIXES.some(
        (prefix) => p === prefix || p.startsWith(`${prefix}/`),
      )
    }
    const isAuthenticated = ref(false)
    const user = ref(null)
    const currentUserType = ref(null)
    
    // Inicializar región al cargar la app
    initializeRegion()

    // Selector de región compacto mobile
    const showRegionPickerMobile = ref(false)
    const regionPickerMobileRef = ref(null)

    const changeRegionMobile = (newRegionId) => {
      setRegion(newRegionId)
      showRegionPickerMobile.value = false
    }

    const handleRegionPickerClickOutside = (event) => {
      if (regionPickerMobileRef.value && !regionPickerMobileRef.value.contains(event.target)) {
        showRegionPickerMobile.value = false
      }
    }

    const isUserMenuOpen = ref(false)
    const mobileMenuOpen = ref(false)
    const mdMenuOpen = ref(false)
    const isMobile = ref(window.innerWidth < 640)
    /** Mismo breakpoint que SearchPageLayout (layout compacto del buscador coliving). */
    const isColivingSearchCompactViewport = ref(window.innerWidth < 1024)
    const isLoadingAuth = ref(true)
    const isInitialAuthCheckComplete = ref(false)
    // Variable para controlar la visibilidad del banner de extensión
    const showExtensionBanner = ref(false)
    // Variable para controlar si hay una actualización disponible
    const updateAvailable = ref(false)
    const swVersion = ref(null)
    const isOnline = ref(true)
    const routerViewKey = ref(1)
    // Variables para el modal de onboarding
    const isOnboardingModalOpen = ref(false)
    const isOnboardingInProgress = ref(false)
    
    // Variable para el modal de "Añade tu coliving" (UpgradeToProModal)
    const isAddColivingModalOpen = ref(false)
    const isPartnerServicesModalOpen = ref(false)
    const partnerServicesInitialService = ref('marketplace')
    
    // Detectar si estamos en modo widget
    const isWidgetMode = computed(() => route.meta.isWidget === true || route.path.startsWith('/widget/') || route.meta.hideNavbar === true);
    
    const showFooter = computed(() => !route.path.startsWith('/agent-dashboard') && !route.path.startsWith('/owner-dashboard') && !route.path.startsWith('/user-dashboard') && !route.path.startsWith('/contracts') && !route.path.startsWith('/encuentratucoliving') && !route.path.startsWith('/findyourcoliving') && !route.path.startsWith('/residencias') && !isWidgetMode.value);

    const showLegalFooterStrip = computed(() => {
      if (isWidgetMode.value || showFooter.value) return false;
      if (isColivingSearchCompactViewport.value) return false;
      const path = route.path;
      return path.startsWith('/encuentratucoliving')
        || path.startsWith('/findyourcoliving')
        || path.startsWith('/residencias');
    });

    /** Buscador coliving en móvil/tablet: layout a pantalla completa sin hueco inferior. */
    const isColivingHubCompactLayout = computed(
      () => isColivingHubRoute(route.path) && isColivingSearchCompactViewport.value,
    );

    // Botón volver arriba
    const showScrollToTop = ref(false);
    const handleScrollTop = () => { showScrollToTop.value = window.scrollY > 400; };
    onMounted(() => window.addEventListener('scroll', handleScrollTop, { passive: true }));
    onUnmounted(() => window.removeEventListener('scroll', handleScrollTop));
    const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Computed para obtener la ciudad desde la ruta
    const currentCityFromRoute = computed(() => {
      if (route.path.startsWith('/encuentratucoliving/') && route.params.city) {
        return SLUG_TO_NAME[route.params.city.toLowerCase()] || null;
      }
      return null;
    });
    
    // Variables para las notificaciones
    const notifications = ref([])
    const notificationSubscription = ref(null)
    const isUserOwner = ref(false)
    
    // Variables para el branding de la agencia
    const agencyName = ref('Coolliving')
    const isLoadingAgencyInfo = ref(false)
    
    // NUEVO: Variables para información del agente y control de logo
    const { agentInfo, isLoading: isLoadingAgentInfo } = useAgentInfo()
    const isAgentInfoReady = ref(false)
    
    // Variables para el banner de extensión mejorado
    const extensionBannerDismissed = ref(false)
    const extensionBannerDelay = ref(false)
    
    // Variables para el banner de lead magnet
    const showLeadMagnetBanner = ref(false)
    const leadMagnetBannerDismissed = ref(false)
    
    // Variable para el dropdown de "Gestiona tu coliving"
    const showManageDropdown = ref(false)

    // Lista de rutas que deberían cachearse para mejor rendimiento
    const cachedRoutes = ref(['Landing', 'BlogPage', 'Contracts'])
    
    // NUEVO: Computed property para determinar si mostrar logo de agencia
    const shouldShowAgencyLogo = computed(() => {
      // Solo mostrar logo de agencia si:
      // 1. Es un agente autenticado
      // 2. Tiene rol master o associated
      // 3. El agencyName no es 'notarial'
      // 4. La información del agente está cargada
      return currentUserType.value === 'agent' && 
             isAgentInfoReady.value &&
             agentInfo.value && 
             (agentInfo.value.agentRole === AGENT_ROLES.MASTER || 
              agentInfo.value.agentRole === AGENT_ROLES.ASSOCIATED) &&
             agencyName.value !== 'Coolliving'
    })
    
    // NUEVO: Watcher para marcar cuando la información del agente esté lista
    watch([agentInfo, isLoadingAgentInfo, currentUserType], ([newAgentInfo, loading, userType]) => {
      // Marcar como ready cuando:
      // - No esté cargando Y
      // - (Tengamos agentInfo O no sea un agente)
      isAgentInfoReady.value = !loading && (newAgentInfo || userType !== 'agent')
      
      // Si el agentInfo acaba de cargarse y es un agente asociado, actualizar el agencyName
      if (!loading && newAgentInfo && userType === 'agent' && 
          newAgentInfo.agentRole === AGENT_ROLES.ASSOCIATED && 
          agencyName.value === 'Coolliving') {
        fetchAgencyInfo()
      }
    }, { immediate: true })

    const componentKey = computed(() => {
      if (route.path.startsWith('/agent-dashboard')) {
        return '/agent-dashboard'; // Clave fija para el layout del agente
      }
      return route.path; // Comportamiento por defecto para otras rutas
    });
    
    // Funciones para las transiciones de página
    const beforePageLeave = () => {
      // Podríamos guardar la posición del scroll antes de salir de la página
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('scrollPosition', window.scrollY.toString())
      }
    }
    
    const onPageEnter = () => {
      // Marcar performance
      if (typeof window !== 'undefined' && 'performance' in window) {
        performance.mark('page-rendered')
        
        // Notificar para analíticas si estamos en producción
        if (process.env.NODE_ENV === 'production' && 'gtag' in window) {
          // Aquí se podría implementar analíticas si se usa
        }
      }
    }

    // Función para guardar el estado de usuario en caché
    const cacheUserState = (authUser) => {
      if (authUser) {
        // Almacenar información básica no sensible
        localStorage.setItem('userAuthState', JSON.stringify({
          isAuthenticated: true,
          displayName: authUser.displayName,
          email: authUser.email,
          photoURL: authUser.photoURL,
        }));
      } else {
        localStorage.removeItem('userAuthState');
      }
    };

    // Intentar cargar el estado de usuario desde caché al inicio
    const cachedAuth = localStorage.getItem('userAuthState');
    if (cachedAuth) {
      try {
        const cachedAuthData = JSON.parse(cachedAuth);
        isAuthenticated.value = cachedAuthData.isAuthenticated;
        // Usamos datos básicos del caché mientras esperamos la verificación completa
        user.value = {
          displayName: cachedAuthData.displayName,
          email: cachedAuthData.email,
          photoURL: cachedAuthData.photoURL
        };
      } catch (e) {
        console.error("Error al analizar la caché de autenticación:", e);
      }
    }

    // Determinar tipo de usuario y guardarlo en caché
    const checkUserType = async (userId) => {
      try {
        if (!userId) {
          currentUserType.value = null;
          return null;
        }
        
        // La verificación del tipo de usuario se hace directamente desde Firestore
        
        const userDocRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userTypeFromDB = userData.userType || 'owner';

          if (['suspended', 'deleted'].includes(userData.accountStatus)) {
            try {
              const { getAuth, signOut } = await import('firebase/auth');
              await signOut(getAuth());
            } catch (signOutError) {
              console.warn('[App] No se pudo cerrar sesión de cuenta deshabilitada:', signOutError);
            }
            currentUserType.value = null;
            isAuthenticated.value = false;
            return null;
          }
          
          // Verificar y corregir el tipo de usuario si es necesario
          let finalUserType = userTypeFromDB;
          
          // Solo verificar tipo de usuario si:
          // 1. El usuario es 'owner' Y no tiene managedOwnerId definido
          // 2. Esto evita consultas innecesarias a managedOwners para propietarios normales
          const needsVerification = userTypeFromDB === 'owner' && 
                                   !userData.managedOwnerId && 
                                   !userData.skipManagedOwnerCheck;
          
          if (needsVerification) {
            try {
              const wasFixed = await verifyAndFixUserType(userId);
              if (wasFixed) {
                // Volver a verificar el tipo después de la corrección
                const updatedUserDoc = await getDoc(userDocRef);
                if (updatedUserDoc.exists()) {
                  finalUserType = updatedUserDoc.data().userType || 'owner';
                }
              }
            } catch (fixError) {
              console.error('[App] Error al verificar/corregir tipo de usuario:', fixError);
              // Marcar que no necesita más verificaciones para este usuario
              // (esto evita bucles infinitos de errores)
              try {
                await updateDoc(userDocRef, { 
                  skipManagedOwnerCheck: true 
                });
              } catch (updateError) {
                console.error('[App] Error al marcar skipManagedOwnerCheck:', updateError);
              }
            }
          }
          
          // Actualizar estado
          currentUserType.value = finalUserType;
          isUserOwner.value = finalUserType === 'owner';
          
          // Guardar en caché
          const authUserForCache = JSON.parse(localStorage.getItem('authUser') || '{}');
          authUserForCache.userType = finalUserType;
          localStorage.setItem('authUser', JSON.stringify(authUserForCache));
          
          return finalUserType;
        }
        
        // Si no existe el documento del usuario, retornar null
        // Se debe crear manualmente el documento admin en Firestore si es necesario
        
        currentUserType.value = null;
        return null;
      } catch (error) {
        console.error('Error al comprobar tipo de usuario:', error);
        currentUserType.value = null;
        return null;
      }
    };
    
    // ⚡ LAZY LOAD: Función para obtener información de la agencia
    const fetchAgencyInfo = async () => {
      if (!isAuthenticated.value || currentUserType.value !== 'agent') {
        agencyName.value = 'Coolliving';
        return;
      }
      
      try {
        isLoadingAgencyInfo.value = true;
        // Cargar Firebase Auth dinámicamente
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        const currentUser = auth.currentUser;
        
        if (!currentUser) {
          agencyName.value = 'Coolliving';
          return;
        }
        
        // Si es un agente asociado, obtener el agencyName del maestro
        if (agentInfo.value && agentInfo.value.agentRole === AGENT_ROLES.ASSOCIATED && agentInfo.value.masterAgentId) {
          try {
            const masterDocRef = doc(db, 'users', agentInfo.value.masterAgentId);
            const masterDoc = await getDoc(masterDocRef);
            
            if (masterDoc.exists()) {
              const masterData = masterDoc.data();
              agencyName.value = masterData.agencyName || 'Coolliving';
              
              // Guardar en caché local
              localStorage.setItem('agencyBranding', JSON.stringify({
                agencyName: agencyName.value,
                timestamp: Date.now()
              }));
              
              isLoadingAgencyInfo.value = false;
              return;
            }
          } catch (error) {
            console.error('Error al obtener información del agente maestro:', error);
          }
        }
        
        // Para agentes maestros o si falló obtener del maestro, usar el endpoint
        const token = await currentUser.getIdToken();
        const response = await fetch('/api/agents/profile', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          agencyName.value = data.agencyName || 'Coolliving';
          
          // Guardar en caché local
          localStorage.setItem('agencyBranding', JSON.stringify({
            agencyName: agencyName.value,
            timestamp: Date.now()
          }));
        } else {
          agencyName.value = 'Coolliving';
        }
      } catch (error) {
        console.error('Error al obtener información de la agencia:', error);
        agencyName.value = 'Coolliving';
      } finally {
        isLoadingAgencyInfo.value = false;
      }
    };
    
    // Inicializar suscripción a notificaciones
    // Retorna una promesa para poder esperar a que se determine el tipo de usuario
    const initializeNotifications = async (userId) => {
      // Limpiar suscripción anterior si existe
      if (notificationSubscription.value) {
        notificationSubscription.value();
        notificationSubscription.value = null;
      }
      
      // Suscribirse a notificaciones para cualquier usuario autenticado
      notificationSubscription.value = subscribeToNotifications((newNotifications) => {
        notifications.value = newNotifications;
      });
      
      // Verificar el tipo de usuario y guardar en caché
      // IMPORTANTE: Esperar a que se determine el tipo antes de continuar
      const userType = await checkUserType(userId);
      
        // Obtener información de la agencia si es agente
        if (userType === 'agent') {
          // Intentar cargar desde caché primero
          const cachedBranding = localStorage.getItem('agencyBranding');
          if (cachedBranding) {
            try {
              const parsed = JSON.parse(cachedBranding);
              // Usar caché si es menor a 1 hora
              if (Date.now() - parsed.timestamp < 3600000) {
                agencyName.value = parsed.agencyName || 'Coolliving';
              } else {
                fetchAgencyInfo();
              }
            } catch (e) {
              fetchAgencyInfo();
            }
          } else {
            fetchAgencyInfo();
          }
        } else {
          agencyName.value = 'Coolliving';
        }
        
        // Redirigir según el tipo de usuario si está en la landing page
        // NOTA: No redirigir si ya estamos en las rutas correctas para evitar loops
        const currentPath = window.location.pathname;
        
        if (currentPath === '/' && !isOnboardingInProgress.value) {
          if (userType === 'agent') {
            router.push('/agent-dashboard');
          } else if (userType === 'admin') {
            router.push('/admin-panel');
          } else if (userType === 'managedOwner') {
            router.push('/owner-dashboard');
          } else {
            router.push('/contracts');
          }
        } else if (userType === 'agent' && currentPath === '/contracts') {
          // Si un agente fue redirigido incorrectamente a /contracts, corregir
          router.push('/agent-dashboard');
        } else if (userType === 'admin' && (currentPath === '/contracts' || currentPath.startsWith('/agent-dashboard'))) {
          // Si un admin fue redirigido incorrectamente, corregir
          router.push('/admin-panel');
        } else if (userType === 'managedOwner' && currentPath === '/contracts') {
          // Si un managedOwner fue redirigido incorrectamente a /contracts, corregir
          router.push('/owner-dashboard');
        }
    };
    
    // Manejadores para acciones de notificaciones
    const handleMarkAsRead = async (notificationId) => {
      try {
        await markNotificationAsRead(notificationId)
        // Actualizar la notificación localmente
        const index = notifications.value.findIndex(n => n.id === notificationId)
        if (index !== -1) {
          notifications.value[index].read = true
          // Forzar actualización reactiva
          notifications.value = [...notifications.value]
        }
      } catch (error) {
        console.error('Error al marcar notificación como leída:', error)
      }
    }
    
    const handleMarkAllAsRead = async () => {
      try {
        await markAllNotificationsAsRead()
        // Actualizar todas las notificaciones localmente
        notifications.value = notifications.value.map(n => ({
          ...n,
          read: true
        }))
      } catch (error) {
        console.error('Error al marcar todas las notificaciones como leídas:', error)
      }
    }
    
    const handleDeleteNotification = async (notificationId) => {
      try {
        await deleteNotification(notificationId)
        // Eliminar la notificación localmente
        notifications.value = notifications.value.filter(n => n.id !== notificationId)
      } catch (error) {
        console.error('Error al eliminar notificación:', error)
      }
    }
    
    const handleViewNotification = (notification) => {
      // Ya se maneja en el componente NotificationsMenu
    }

    // ⚡ LAZY LOAD: Función verificar autenticación con carga dinámica de Firebase Auth
    // Variable para asegurar que solo registramos el listener una vez
    let authListenerRegistered = false;
    
    const checkAuth = async () => {
      // Evitar registrar múltiples listeners
      if (authListenerRegistered) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[App.vue] Auth listener ya está registrado, saltando...');
        }
        return;
      }
      
      // Cargar Firebase Auth solo cuando sea necesario
      const { getAuth, onAuthStateChanged } = await import('firebase/auth');
      const auth = getAuth();
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[App.vue] Registrando listener de autenticación...');
      }
      authListenerRegistered = true;
      
      onAuthStateChanged(auth, async (authUser) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[App.vue] Estado de autenticación cambió:', authUser ? 'autenticado' : 'no autenticado');
        }
        isLoadingAuth.value = true;
        
        
        
        if (authUser) {
          // El usuario está autenticado
          isAuthenticated.value = true;
          user.value = authUser;
          
          localStorage.setItem('authUser', JSON.stringify({
            uid: authUser.uid,
            email: authUser.email,
            displayName: authUser.displayName
          }));
          
          // IMPORTANTE: Esperar a que se determine el tipo de usuario
          // antes de marcar isInitialAuthCheckComplete como true
          await initializeNotifications(authUser.uid);
          
          cacheUserState(authUser);
        } else {
          // El usuario no está autenticado
          isAuthenticated.value = false;
          user.value = null;
          currentUserType.value = null;
          agencyName.value = 'Coolliving'; // Reset agency branding
          
          // Limpiar notificaciones y suscripción
          if (notificationSubscription.value) {
            notificationSubscription.value();
            notificationSubscription.value = null;
          }
          notifications.value = [];
          
          // Limpiar la caché
          cacheUserState(null);
          localStorage.removeItem('authUser');
          localStorage.removeItem('agencyBranding'); // Limpiar caché de branding
          
          // Check for dev mode auth user
          const devAuthUser = sessionStorage.getItem('dev_auth_user');
          if (devAuthUser) {
            const parsedDevUser = JSON.parse(devAuthUser);
            user.value = parsedDevUser;
            isAuthenticated.value = true;
            currentUserType.value = parsedDevUser.userType || 'owner';
            
            localStorage.setItem('authUser', JSON.stringify({
              uid: user.value.uid,
              email: user.value.email,
              displayName: user.value.displayName,
              userType: currentUserType.value
            }));
            
            await initializeNotifications(user.value.uid);
          }
        }
        
        isLoadingAuth.value = false;
        isInitialAuthCheckComplete.value = true;
      });
    };

    // Watch para redireccionar al usuario cuando cambia la ruta
    watch(() => route.path, (newPath) => {
      // Verificar la URL actual del navegador, no solo newPath
      const currentPath = window.location.pathname;
      
      // Solo redirigir si estamos en la página de inicio y el usuario está autenticado
      // y NO si están accediendo a una URL específica de contrato
      // Y NO si es usuario admin, agent, owner o managedOwner (tienen dashboards propios)
      // Y NO redirigir si está en proceso de onboarding
      if (currentPath === '/' && isAuthenticated.value && !isLoadingAuth.value && 
          !currentPath.startsWith('/contracts/') && 
          currentUserType.value !== 'admin' &&
          currentUserType.value !== 'agent' &&
          currentUserType.value !== 'managedOwner' &&
          !isOnboardingInProgress.value) {
        router.push('/contracts')
      }
    })

    // Toggle user menu
    const toggleUserMenu = () => {
      isUserMenuOpen.value = !isUserMenuOpen.value
    }

    // Toggle mobile menu
    const toggleMobileMenu = () => {
      mobileMenuOpen.value = !mobileMenuOpen.value
    }

    // Función para manejar "Gestiona tu coliving" en menú MD
    const showManageDropdownInMd = () => {
      mdMenuOpen.value = false
      // Redirigir a signup con userType=agent
      router.push('/signup?userType=agent')
    }

    // Listen for clicks outside the user menu to close it
    const closeMenus = (e) => {
      // Check if the click is outside the dropdown (user menu)
      const dropdown = document.querySelector('.dropdown-container')
      if (dropdown && !dropdown.contains(e.target)) {
        isUserMenuOpen.value = false
      }
      
      // Check if the click is outside the manage dropdown
      const manageDropdown = document.querySelector('.dropdown-manage-container')
      if (manageDropdown && !manageDropdown.contains(e.target)) {
        showManageDropdown.value = false
      }
    }

    // Check window resize for mobile/desktop view
    const checkWindowSize = () => {
      isMobile.value = window.innerWidth < 640
      isColivingSearchCompactViewport.value = window.innerWidth < 1024
    }

    // ⚡ LAZY LOAD: Logout function con carga dinámica de Firebase Auth
    const handleLogout = async () => {
      try {
        // Check if we are in dev mode with simulated auth
        if (sessionStorage.getItem('dev_auth_user')) {
          sessionStorage.removeItem('dev_auth_user')
          isAuthenticated.value = false
          user.value = null
          currentUserType.value = null
          agencyName.value = 'Coolliving' // Reset agency branding
          // Limpiar la caché al cerrar sesión
          cacheUserState(null);
          localStorage.removeItem('agencyBranding'); // Limpiar caché de branding
          localStorage.removeItem('authUser'); // Limpiar datos de usuario cacheados
          localStorage.removeItem('userAuthState'); // Limpiar estado de autenticación
          router.push('/')
          return
        }

        // Eliminar FCM token del backend antes de cerrar sesión
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        await removeFcmTokenFromBackend().catch(() => { /* no crítico en logout */ });

        // Cargar Firebase Auth solo cuando sea necesario
        const { getAuth, signOut } = await import('firebase/auth');
        const auth = getAuth()
        await signOut(auth)
        
        // Limpiar TODOS los datos de sesión al cerrar sesión
        cacheUserState(null);
        localStorage.removeItem('agencyBranding'); // Limpiar caché de branding
        localStorage.removeItem('authUser'); // Limpiar datos de usuario cacheados
        localStorage.removeItem('userAuthState'); // Limpiar estado de autenticación
        agencyName.value = 'Coolliving' // Reset agency branding
        
        // Limpiar estados reactivos
        isAuthenticated.value = false
        user.value = null
        currentUserType.value = null
        
        router.push('/')
      } catch (error) {
        console.error('Error during logout:', error)
      }
    }

    // Scroll functions for landing page sections with smart routing
    const scrollToSection = (selector) => {
      mobileMenuOpen.value = false;
      
      // Verificar si estamos en la página principal
      if (route.path !== '/') {
        
        // Guardar el selector en sessionStorage para usarlo después de la navegación
        sessionStorage.setItem('scrollToSectionAfterNavigation', selector);
        
        // Navegar a la página principal
        router.push('/');
      } else {
        // Ya estamos en la página principal, simplemente hacer scroll
        setTimeout(() => {
          const element = document.querySelector(selector);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    }

    const scrollToComparison = () => scrollToSection('#comparison-table')
    const scrollToHowItWorks = () => scrollToSection('#step-by-step-guide')
    const scrollToFaqs = () => scrollToSection('#faqs-section')
    const scrollToPrivacy = () => scrollToSection('#privacy-section')

    // Mobile versions of scroll functions (close menu first)
    const scrollToSectionMobile = (selector) => {
      mobileMenuOpen.value = false;
      
      // Verificar si estamos en la página principal
      if (route.path !== '/') {
        
        // Guardar el selector en sessionStorage para usarlo después de la navegación
        sessionStorage.setItem('scrollToSectionAfterNavigation', selector);
        
        // Navegar a la página principal
        router.push('/');
      } else {
        // Ya estamos en la página principal, simplemente hacer scroll con un poco más de retraso para móviles
        setTimeout(() => {
          const element = document.querySelector(selector);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      }
    }

    const scrollToComparisonMobile = () => scrollToSectionMobile('#comparison-table')
    const scrollToHowItWorksMobile = () => scrollToSectionMobile('#step-by-step-guide')
    const scrollToFaqsMobile = () => scrollToSectionMobile('#faqs-section')
    const scrollToPrivacyMobile = () => scrollToSectionMobile('#privacy-section')

    // Navigation to login with redirect
    const navigateToLogin = () => {
      const currentPath = route.path
      if (currentPath !== '/login') {
        if (currentPath !== '/') {
          // Save current path to redirect back after login
          sessionStorage.setItem('redirectAfterLogin', currentPath)
        }
        router.push('/login')
      }
    }

    // Cookies handlers (consentimiento gestionado en CookiesBanner + analyticsConsent.js)
    const handleCookiesAccept = () => {
      /* grantAllCookieConsent se ejecuta en CookiesBanner */
    }

    const handleCookiesReject = () => {
      /* revokeOptionalCookieConsent se ejecuta en CookiesBanner */
    }

    // Función para forzar la actualización de la aplicación
    const refreshApp = () => {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        // Enviar mensaje al service worker para aplicar la actualización
        navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
      } else {
        // Si no hay service worker, simplemente recargar la página
        window.location.reload();
      }
      updateAvailable.value = false;
    }

    // Función para verificar la versión del service worker
    const checkServiceWorkerVersion = () => {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        // Crear un canal de comunicación
        const messageChannel = new MessageChannel();
        
        // Configurar el canal para recibir la respuesta
        messageChannel.port1.onmessage = (event) => {
          if (event.data && event.data.version) {
            swVersion.value = event.data.version;
          }
        };
        
        // Enviar el mensaje al service worker
        navigator.serviceWorker.controller.postMessage(
          { type: 'GET_VERSION' },
          [messageChannel.port2]
        );
      }
    }

    // Función para monitorear el estado de la conexión
    const monitorOnlineStatus = () => {
      isOnline.value = navigator.onLine;
      
      window.addEventListener('online', () => {
        isOnline.value = true;
      });
      
      window.addEventListener('offline', () => {
        isOnline.value = false;
      });
    }

    // Añadir función para forzar actualización cuando proviene de /blog
    let lastPathWasBlog = false;
    let lastPathWasColivingSearch = false;

    watch(() => route.fullPath, (newPath, oldPath) => {
      const isFromAdminBlog = oldPath && oldPath.startsWith('/admin-panel/blog');
      const isFromBlogPage = oldPath && oldPath.startsWith('/blog');
      const isFromColivingSearch = oldPath && isColivingHubRoute(oldPath);
      const isToColivingSearch = newPath && isColivingHubRoute(newPath);
      // Solo considerar "query-only nav" cuando el PATH es idéntico (ej: ?page=2 → ?page=3).
      // Navegación entre paths distintos (hub → ciudad, ciudad → ciudad) SÍ necesita remount.
      const colivingQueryOnlyNav = isFromColivingSearch && isToColivingSearch
        && stripRoutePath(newPath) === stripRoutePath(oldPath);

      // Si navegamos DESDE el panel de admin del blog, la página pública del blog, o ColivingAdvancedSearch
      // incrementamos la key para forzar la recarga del componente destino.
      // Excepción: ?page=2 dentro del buscador no debe remontar todo (doble API + pérdida de estado).
      if ((isFromAdminBlog || isFromBlogPage || isFromColivingSearch) && newPath !== oldPath && !colivingQueryOnlyNav) {
        routerViewKey.value++; 
      }
      
      // Verificar si la navegación proviene de /blog y almacenar para verificación posterior
      if (oldPath && oldPath.startsWith('/blog')) {
        lastPathWasBlog = true;
        
        // Programar verificación de contenido
        setTimeout(checkContentAfterBlogNavigation, 500);
      } else {
        lastPathWasBlog = false;
      }
      
      // Nueva lógica similar para navegación desde coliving search (incl. /residencias)
      if (oldPath && isColivingHubRoute(oldPath)) {
        lastPathWasColivingSearch = true;
        
        // Programar verificación de contenido similar a blog
        setTimeout(checkContentAfterColivingNavigation, 500);
      } else {
        lastPathWasColivingSearch = false;
      }
    });

    // Función para verificar problemas de renderizado después de navegar desde /blog
    function checkContentAfterBlogNavigation() {
      if (!lastPathWasBlog) return;
      
      const contentArea = document.querySelector('main') || document.querySelector('.min-h-screen');
      
      // Verificar si hay contenido visible
      if (contentArea && contentArea.children.length === 0) {
        // Intentar forzar una actualización
        nextTick(() => {
          // Aplicar una solución temporal: recargar la página
          if (lastPathWasBlog && !route.path.startsWith('/blog')) {
            window.location.reload();
          }
        });
      }
    }

    // Función para verificar problemas de renderizado después de navegar desde ColivingAdvancedSearch
    function checkContentAfterColivingNavigation() {
      if (!lastPathWasColivingSearch) return;
      
      const contentArea = document.querySelector('main') || document.querySelector('.min-h-screen');
      
      // Verificar si hay contenido visible
      if (contentArea && contentArea.children.length === 0) {
        // Intentar forzar una actualización
        nextTick(() => {
          // Aplicar una solución temporal: recargar la página
          if (lastPathWasColivingSearch && !isColivingHubRoute(route.fullPath)) {
            window.location.reload();
          }
        });
      }
    }

    // Función para ejecutar scroll pendiente después de navegación
    const executeScrollAfterNavigation = () => {
      const pendingScroll = sessionStorage.getItem('scrollToSectionAfterNavigation');
      
      if (pendingScroll && route.path === '/') {
        // Limpiar el pendiente para evitar scrolls inesperados
        sessionStorage.removeItem('scrollToSectionAfterNavigation');
        
        // Esperar a que el componente esté completamente cargado
        setTimeout(() => {
          const element = document.querySelector(pendingScroll);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 500); // Retraso mayor para asegurar que el componente esté montado
      }
    }

    // Función nueva para logout y cerrar menú móvil
    const logoutAndCloseMenu = async () => {
      await handleLogout(); // Espera a que termine el logout
      mobileMenuOpen.value = false; // Cierra el menú
    }

    // Función mejorada para controlar el banner de extensión
    const toggleExtensionBanner = () => {
      showExtensionBanner.value = !showExtensionBanner.value
      
      // Si se activa manualmente, activar también el delay y marcar que no está dismissed
      if (showExtensionBanner.value) {
        extensionBannerDelay.value = true // ✅ Activar delay para mostrar
        extensionBannerDismissed.value = false
        localStorage.removeItem('extensionBannerDismissed')
      } else {
        // Si se desactiva manualmente, resetear el delay también
        extensionBannerDelay.value = false
      }
    }
    
    // Función para cerrar el banner y recordar la preferencia
    const dismissExtensionBanner = () => {
      showExtensionBanner.value = false
      extensionBannerDelay.value = false // ✅ Resetear delay al cerrar
      extensionBannerDismissed.value = true
      localStorage.setItem('extensionBannerDismissed', 'true')
    }
    
    // Función para mostrar el banner con delay y animación
    const showExtensionBannerWithDelay = () => {
      // Solo mostrar si no ha sido dismissed previamente
      const wasDismissed = localStorage.getItem('extensionBannerDismissed') === 'true'
      
      if (!wasDismissed && route.path === '/') {
        // Esperar 2 segundos para que la página cargue completamente
        setTimeout(() => {
          if (route.path === '/') { // Verificar que sigue en la home
            extensionBannerDelay.value = true
            // Pequeño delay adicional para la animación
            setTimeout(() => {
              showExtensionBanner.value = true
            }, 100)
          }
        }, 2000)
      }
    }

    // Función para alternar el banner desde el menú móvil (cierra el menú móvil)
    const toggleExtensionBannerMobile = () => {
      showExtensionBanner.value = !showExtensionBanner.value
      mobileMenuOpen.value = false
    }
    
    // Funciones para el Lead Magnet Banner
    const dismissLeadMagnetBanner = () => {
      showLeadMagnetBanner.value = false
      leadMagnetBannerDismissed.value = true
      localStorage.setItem('leadMagnetBannerDismissed', 'true')
    }
    
    const handleOpenLeadMagnetFromBanner = () => {
      // Enviar evento personalizado para que ColivingAdvancedSearch abra el modal
      window.dispatchEvent(new CustomEvent('open-lead-magnet-modal'))
    }
    
    // Función para toggle del dropdown "Gestiona tu coliving"
    const toggleManageDropdown = () => {
      showManageDropdown.value = !showManageDropdown.value
    }
    
    // Función para abrir el modal de "Añade tu coliving" desde el dropdown
    const openAddColivingModal = () => {
      showManageDropdown.value = false // Cerrar dropdown
      isAddColivingModalOpen.value = true
    }

    // Handler para click en "Servicios para empresas" → modal global (PartnerContactModal)
    const handlePartnerServicesClick = (source) => {
      openPartnerServicesModal({
        source,
        serviceKey: 'marketplace',
        user: user.value,
      })
    }

    function onOpenPartnerServicesEvent(event) {
      partnerServicesInitialService.value = event?.detail?.serviceKey || 'marketplace'
      isPartnerServicesModalOpen.value = true
    }

    onMounted(() => {
      window.addEventListener(OPEN_PARTNER_SERVICES_EVENT, onOpenPartnerServicesEvent)
    })

    onUnmounted(() => {
      window.removeEventListener(OPEN_PARTNER_SERVICES_EVENT, onOpenPartnerServicesEvent)
    })

    // Inicializar evento para escuchar start-owner-onboarding
    onMounted(() => {
      document.addEventListener('mousedown', handleRegionPickerClickOutside)

      // Helper de consola para forzar guardado del FCM token (debug/admin)
      window.__saveFcmToken = requestAndSavePushToken;

      // Escuchar mensajes push en foreground (app abierta)
      // Cuando la app está visible mostramos una notificación nativa (permiso ya concedido)
      if (isFcmEnabled()) {
        setupForegroundMessages((payload) => {
          const n = payload.notification || {};
          const data = payload.data || {};
          if ('Notification' in window && Notification.permission === 'granted') {
            const notif = new Notification(n.title || 'CoolLiving', {
              body: n.body || '',
              icon: '/images/favicon.jpg',
              tag: data.tag || 'coolliving-fg',
              data: { url: data.url || '/' },
            });
            notif.onclick = () => {
              window.focus();
              notif.close();
            };
          }
        });
      }
      // ⚡ LAZY LOAD: Cargar Firebase Auth solo si es necesario
      const cachedAuth = localStorage.getItem('userAuthState');
      const requiresAuth = route.meta.requiresAuth;
      const isAuthRoute = route.path.includes('/login') || route.path.includes('/signup');
      
      // Variable para controlar si ya hemos inicializado auth
      let authInitialized = false;
      
      // Cargar Firebase Auth solo si:
      // 1. Usuario está autenticado (caché) OR
      // 2. Está en una ruta que requiere auth OR
      // 3. Está en login/signup
      if (cachedAuth || requiresAuth || isAuthRoute) {
        checkAuth();
        authInitialized = true;
      } else {
        // NO cargar Firebase Auth inmediatamente para páginas públicas
        // Marcar como completo para evitar skeleton loader
        isInitialAuthCheckComplete.value = true;
        
        // Escuchar navegación a rutas que requieren auth O rutas de autenticación
        watch(() => route.path, (newPath) => {
          const needsAuth = route.meta.requiresAuth || newPath.includes('/login') || newPath.includes('/signup');
          // ✅ FIX: Inicializar auth cuando el usuario navega a login/signup
          // Esto asegura que onAuthStateChanged esté escuchando cuando el usuario haga login
          if (needsAuth && !authInitialized) {
            checkAuth();
            authInitialized = true;
          }
        });
      }
      
      // ✅ Inicializar detección automática de idioma
      Trans.initializeI18n()
      
      // Ocultar el initial loader después de que Vue haya montado
      nextTick(() => {
        const loader = document.querySelector('.initial-loader');
        if (loader) {
          loader.style.opacity = '0';
          loader.style.transition = 'opacity 0.3s ease-out';
          setTimeout(() => {
            loader.style.display = 'none';
          }, 300);
        }
      });
      
      // Manejar redirecciones de blog posts desde middleware
      handleBlogRedirect()
      
      window.addEventListener('resize', checkWindowSize)
      document.addEventListener('mousedown', closeMenus)
      
      // Aseguramos que no haya scroll horizontal
      document.documentElement.style.overflowX = 'hidden'
      document.body.style.overflowX = 'hidden'

      // Monitorear el estado de la conexión
      monitorOnlineStatus();

      // Detección de actualizaciones del service worker
      if ('serviceWorker' in navigator) {
        // Escuchar el evento 'controllerchange' del service worker
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          updateAvailable.value = true;
        });

        // Escuchar mensajes del service worker
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data && event.data.type === 'SW_ACTIVATED') {
            swVersion.value = event.data.version;
          }
        });

        // Limpiar posibles registros obsoletos (e.g. firebase-messaging-sw.js)
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((r) => {
            const url = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || '';
            if (url.includes('firebase-messaging-sw')) r.unregister();
          });
        }).catch(() => { /* no crítico */ });

        // Registrar el service worker principal si no está ya registrado
        navigator.serviceWorker.register('/service-worker.js').catch((err) => {
          console.warn('[SW] Error al registrar service-worker.js:', err);
        });

        // Comprobar si hay un service worker instalado pero esperando
        navigator.serviceWorker.getRegistrations().then((regs) => {
          const registration = regs.find((r) => {
            const url = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || '';
            return url.includes('service-worker.js');
          });
          if (registration) {
            // Comprobar versión actual
            checkServiceWorkerVersion();
            
            if (registration.waiting) {
              // Hay una nueva versión instalada pero esperando
              updateAvailable.value = true;
            }
            
            // Verificar periódicamente si hay actualizaciones del service worker
            // Verificar cada 30 minutos si hay una nueva versión disponible
            const checkInterval = 30 * 60 * 1000; // 30 minutos
            
            const checkForUpdates = () => {
              registration.update().catch(err => {
                console.error('Error al verificar actualizaciones del service worker:', err);
              });
            };
            
            // Verificar actualizaciones inmediatamente al cargar la página y luego periódicamente
            checkForUpdates();
            setInterval(checkForUpdates, checkInterval);
            
            // Detectar cuando se instala una actualización
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing;
              
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    // Nueva versión instalada y lista para activarse
                    updateAvailable.value = true;
                    // Obtener versión actualizada
                    checkServiceWorkerVersion();
                  }
                });
              }
            });
          }
        });
      }

      // Verificar si hay un scroll pendiente
      executeScrollAfterNavigation();

      // Agregar evento para escuchar cuando se inicia el proceso de onboarding
      window.addEventListener('start-owner-onboarding', (event) => {
        // Si el usuario ya está autenticado y es owner, llevarlo directamente a crear contrato
        if (isAuthenticated.value && user.value) {
          const formData = event.detail;
          
          // Asegurarnos de incluir los parámetros incluso si están vacíos
          const queryParams = new URLSearchParams({
            contractType: formData.type || 'rent',
            propertyLocation: formData.location || '',
            propertyPrice: formData.price ? formData.price.toString() : ''
          }).toString();
          
          // Navegar directamente a la página de crear contrato
          const url = `/contracts/add?${queryParams}`;
          window.location.href = url;
        } else {
          // Si no está autenticado, abrir el modal de onboarding y activar el flag
          isOnboardingModalOpen.value = true;
          isOnboardingInProgress.value = true;
        }
      });
      
      // Cargar preferencia del banner de extensión
      extensionBannerDismissed.value = localStorage.getItem('extensionBannerDismissed') === 'true'
      
      // Mostrar el banner con delay solo en la ruta principal
      if (route.path === '/') {
        showExtensionBannerWithDelay()
      } else {
        showExtensionBanner.value = false
      }
      
      // Cargar preferencia del banner de lead magnet
      leadMagnetBannerDismissed.value = localStorage.getItem('leadMagnetBannerDismissed') === 'true'
      
      // Banner de lead magnet desactivado
    })

    // Watch mejorado para mostrar/ocultar el banner en la ruta principal
    watch(() => route.path, (newPath) => {
      if (newPath === '/') {
        // Solo mostrar con delay si no está dismissed
        showExtensionBannerWithDelay()
      } else {
        // Ocultar inmediatamente al salir de la home
        showExtensionBanner.value = false
        extensionBannerDelay.value = false
      }
      
      // Banner de lead magnet desactivado
      showLeadMagnetBanner.value = false
    })

    // Función para manejar la finalización del onboarding
    function handleOnboardingComplete(data) {
      // Desactivar el flag de onboarding en progreso después de completarlo
      isOnboardingInProgress.value = false;
      // El propio componente modal ya redirige a la página de creación de contrato
    }

    // Añadir manejador para cuando se cierra el modal sin completar
    function handleOnboardingClose() {
      // Desactivar el flag de onboarding en progreso
      isOnboardingInProgress.value = false;
      isOnboardingModalOpen.value = false;
    }

    // Manejar redirecciones de blog posts desde middleware
    function handleBlogRedirect() {
      const urlParams = new URLSearchParams(window.location.search);
      const postSlug = urlParams.get('post');
      
      if (postSlug) {
        // Limpiar la URL de parámetros
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
        // Navegar al blog post
        router.push(`/${postSlug}`);
      }
    }

    // Función para trackear clicks en navegación
    const handleNavClick = (section, label, destination) => {
      trackNavClick({ section, label, destination })
    }

    return {
      isAuthenticated,
      user,
      currentUserType,
      isUserMenuOpen,
      mobileMenuOpen,
      isMobile,
      showExtensionBanner,
      toggleExtensionBanner,
      toggleExtensionBannerMobile,
      toggleUserMenu,
      toggleMobileMenu,
      mdMenuOpen,
      showManageDropdownInMd,
      handleLogout,
      scrollToComparison,
      scrollToHowItWorks,
      scrollToFaqs,
      scrollToPrivacy,
      scrollToComparisonMobile,
      scrollToHowItWorksMobile,
      scrollToFaqsMobile,
      scrollToPrivacyMobile,
      navigateToLogin,
      route,
      handleCookiesAccept,
      handleCookiesReject,
      updateAvailable,
      refreshApp,
      swVersion,
      isOnline,
      isInitialAuthCheckComplete,
      // Funciones de transición
      beforePageLeave,
      onPageEnter,
      cachedRoutes,
      routerViewKey,
      logoutAndCloseMenu,
      isOnboardingModalOpen,
      isOnboardingInProgress,
      handleOnboardingComplete,
      handleOnboardingClose,
      // Modal "Añade tu coliving"
      isAddColivingModalOpen,
      t, // Exportar t para usarlo en el template
      componentKey, // Exportar la clave computada
      // Notificaciones
      notifications,
      isUserOwner,
      handleMarkAsRead,
      handleMarkAllAsRead,
      handleDeleteNotification,
      handleViewNotification,
      // Branding de agencia
      agencyName,
      isLoadingAgencyInfo,
      fetchAgencyInfo,
      // Banner de extensión mejorado
      extensionBannerDismissed,
      extensionBannerDelay,
      dismissExtensionBanner,
      showExtensionBannerWithDelay,
      showFooter,
      showLegalFooterStrip,
      isColivingHubCompactLayout,
      showScrollToTop,
      scrollToTop,
      // NUEVO: Información del agente para logo
      agentInfo,
      isLoadingAgentInfo,
      isAgentInfoReady,
      shouldShowAgencyLogo,
      // Widget mode
      isWidgetMode,
      // Lead Magnet Banner
      showLeadMagnetBanner,
      leadMagnetBannerDismissed,
      dismissLeadMagnetBanner,
      handleOpenLeadMagnetFromBanner,
      currentCityFromRoute,
      // Dropdown "Gestiona tu coliving"
      showManageDropdown,
      toggleManageDropdown,
      openAddColivingModal,
      // Partner services
      handlePartnerServicesClick,
      isPartnerServicesModalOpen,
      partnerServicesInitialService,
      // Navigation tracking
      handleNavClick,
      // Selector región mobile
      regionId,
      regionFlag,
      showRegionPickerMobile,
      regionPickerMobileRef,
      changeRegionMobile
    }
  }
}
</script>

<style>
/* Ocultar scrollbar vertical pero mantener funcionalidad */
html {
  overflow-y: scroll;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

html::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

body {
  overflow-y: scroll;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

body::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

/* Update z-index hierarchy */
.bg-white.shadow-sm {
  z-index: 50;
  /* Add box shadow for better visual separation */
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  /* Add transition for smooth scroll effect */
  transition: transform 0.3s ease;
}

/* Removed redundant mobile menu styles */

nav {
  position: relative;
}

.dropdown-container {
  position: relative;
  isolation: isolate;
}

.dropdown-container .absolute {
  z-index: 60;
}

/* Important fixes for sticky header */
header.fixed {
  position: fixed !important;
  width: 100% !important;
}

/* Para asegurar que la sección tenga margen superior al hacer scroll */
#profiles-section {
  scroll-margin-top: 80px;
}

#solutions-section {
  scroll-margin-top: 80px;
}

#comparison-table {
  scroll-margin-top: 80px;
}

#privacy-section {
  scroll-margin-top: 80px;
}

#b2b-section {
  scroll-margin-top: 80px;
}

#partner-services-section {
  scroll-margin-top: 80px;
}

#call-section {
  scroll-margin-top: 80px;
}

/* Transiciones de página */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.15s ease-in-out;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}

/* Transición de fundido */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Transición de deslizamiento */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-enter-from {
  transform: translateX(30px);
  opacity: 0;
}

.slide-leave-to {
  transform: translateX(-30px);
  opacity: 0;
}

/* Transición para el modo de contratos */
.contracts-enter-active,
.contracts-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.contracts-enter-from {
  transform: translateY(20px);
  opacity: 0;
}

.contracts-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

/* Asegurarnos de que el contenido principal ocupe todo el ancho en móvil */
@media (max-width: 639px) {
  /* Forzar el body para evitar scroll horizontal */
  body {
    overflow-x: hidden !important;
    width: 100% !important;
    position: relative !important;
  }
  
  /* Evitar scroll horizontal en el app */
  #app {
    overflow-x: hidden !important;
    width: 100% !important;
    position: relative !important;
  }
  
  .mobile-content-wrapper {
    width: 100% !important;
    max-width: 100% !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    box-sizing: border-box !important;
    overflow-x: hidden !important;
  }
  
  /* Ajuste para secciones con 100vw que causan scroll */
  section, 
  .section-container,
  .landing-section {
    width: 100% !important; /* Cambiado de 100vw a 100% */
    max-width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-left: 0.75rem !important;
    padding-right: 0.75rem !important;
    box-sizing: border-box !important;
    overflow-x: hidden !important;
  }
  
  /* Selector específico para hijos directos de router-view */
  .mobile-content-wrapper > :deep(*) > .max-w-7xl,
  .mobile-content-wrapper > :deep(*) > .max-w-6xl,
  .mobile-content-wrapper > :deep(*) > .max-w-5xl,
  .mobile-content-wrapper > :deep(*) > .max-w-4xl,
  .mobile-content-wrapper > :deep(*) > .max-w-3xl,
  .mobile-content-wrapper > :deep(*) > .max-w-2xl,
  .mobile-content-wrapper > :deep(*) > .container {
    width: 100% !important;
    max-width: 100% !important;
    padding-left: 0.75rem !important;
    padding-right: 0.75rem !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  /* Hero home: mismo gutter que la navbar (px-4) */
  .mobile-content-wrapper #hero-section .hero-page-gutter {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }
  
  /* Estilos directos para los componentes de nivel superior */
  router-view > div,
  router-view > section {
    width: 100% !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    overflow-x: hidden !important;
  }
  
  /* Estilo específico para el wrapper de la landing page */
  .landing-wrapper {
    padding: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow-x: hidden !important;
  }
  
  /* Ajustes para centrar el contenido de la landing page */
  .landing-wrapper > :deep(div) {
    margin: 0 auto !important;
    width: 100% !important;
    max-width: 100% !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    overflow-x: hidden !important;
  }
  
  /* Estilo para cada sección de la landing - sin padding lateral para permitir fondos full-bleed */
  .landing-wrapper > :deep(div) > section {
    width: 100% !important;
    max-width: 100% !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    box-sizing: border-box !important;
    overflow-x: hidden !important;
  }
  
  /* Ajuste para el botón heroico en vista móvil */
  .landing-wrapper > :deep(div) > section button {
    max-width: 100% !important;
    width: auto !important;
    white-space: normal !important;
  }
}
</style>